import type { WaitlistInterest } from '../data/site';
import { SITE } from '../data/site';

export interface WaitlistLocationMetadata {
  ip?: string;
  country?: string;
  countryCode?: string;
  city?: string;
  region?: string;
  postal?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  org?: string;
  asn?: string;
}

export interface WaitlistMetadata {
  browser: string;
  browserVersion: string;
  device: 'desktop' | 'mobile' | 'tablet';
  os: string;
  osVersion: string;
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  devicePixelRatio: number;
  colorDepth: number;
  language: string;
  languages: string[];
  timezone: string;
  timezoneOffsetMinutes: number;
  referrer: string;
  landingPage: string;
  submissionPage: string;
  pageTitle: string;
  utm: Record<string, string> | null;
  cookieEnabled: boolean;
  online: boolean;
  hardwareConcurrency: number | null;
  maxTouchPoints: number;
  connectionType: string | null;
  effectiveConnectionType: string | null;
  downlinkMbps: number | null;
  rttMs: number | null;
  saveData: boolean | null;
  prefersReducedMotion: boolean;
  prefersColorScheme: string;
  termsAccepted: boolean;
  interestLabel: string | null;
  collectedAt: string;
  location: WaitlistLocationMetadata | null;
}

export interface WaitlistPayload {
  name: string;
  email: string;
  interest: WaitlistInterest | 'unspecified';
  source: string;
  timestamp: string;
  metadata: WaitlistMetadata;
}

function parseBrowser(ua: string) {
  const edge = ua.match(/Edg\/([\d.]+)/);
  if (edge) return { browser: 'Edge', browserVersion: edge[1] };

  const chrome = ua.match(/Chrome\/([\d.]+)/);
  if (chrome && !ua.includes('Edg/')) return { browser: 'Chrome', browserVersion: chrome[1] };

  const firefox = ua.match(/Firefox\/([\d.]+)/);
  if (firefox) return { browser: 'Firefox', browserVersion: firefox[1] };

  const safari = ua.match(/Version\/([\d.]+).*Safari/);
  if (safari && ua.includes('Safari') && !ua.includes('Chrome')) {
    return { browser: 'Safari', browserVersion: safari[1] };
  }

  if (ua.includes('MSIE') || ua.includes('Trident')) {
    return { browser: 'Internet Explorer', browserVersion: 'unknown' };
  }

  return { browser: 'Unknown', browserVersion: 'unknown' };
}

function parseOs(ua: string) {
  if (/Windows NT 10/.test(ua)) return { os: 'Windows', osVersion: '10/11' };
  if (/Windows NT 6\.3/.test(ua)) return { os: 'Windows', osVersion: '8.1' };
  if (/Windows NT 6\.1/.test(ua)) return { os: 'Windows', osVersion: '7' };
  if (/Mac OS X ([\d_]+)/.test(ua)) {
    const match = ua.match(/Mac OS X ([\d_]+)/);
    return { os: 'macOS', osVersion: match?.[1]?.replace(/_/g, '.') ?? 'unknown' };
  }
  if (/Android ([\d.]+)/.test(ua)) {
    const match = ua.match(/Android ([\d.]+)/);
    return { os: 'Android', osVersion: match?.[1] ?? 'unknown' };
  }
  if (/iPhone OS ([\d_]+)/.test(ua)) {
    const match = ua.match(/iPhone OS ([\d_]+)/);
    return { os: 'iOS', osVersion: match?.[1]?.replace(/_/g, '.') ?? 'unknown' };
  }
  if (/iPad/.test(ua)) return { os: 'iPadOS', osVersion: 'unknown' };
  if (/Linux/.test(ua)) return { os: 'Linux', osVersion: 'unknown' };
  return { os: 'Unknown', osVersion: 'unknown' };
}

function detectDevice(ua: string): WaitlistMetadata['device'] {
  if (/Tablet|iPad/i.test(ua)) return 'tablet';
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return 'mobile';
  return 'desktop';
}

function readUtmParams(): Record<string, string> | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  const utm: Record<string, string> = {};

  for (const key of keys) {
    const value = params.get(key);
    if (value) utm[key] = value;
  }

  return Object.keys(utm).length > 0 ? utm : null;
}

function readConnection() {
  const connection = (
    navigator as Navigator & {
      connection?: {
        type?: string;
        effectiveType?: string;
        downlink?: number;
        rtt?: number;
        saveData?: boolean;
      };
    }
  ).connection;

  if (!connection) {
    return {
      connectionType: null,
      effectiveConnectionType: null,
      downlinkMbps: null,
      rttMs: null,
      saveData: null,
    };
  }

  return {
    connectionType: connection.type ?? null,
    effectiveConnectionType: connection.effectiveType ?? null,
    downlinkMbps: typeof connection.downlink === 'number' ? connection.downlink : null,
    rttMs: typeof connection.rtt === 'number' ? connection.rtt : null,
    saveData: typeof connection.saveData === 'boolean' ? connection.saveData : null,
  };
}

async function getIpLocation(): Promise<WaitlistLocationMetadata | null> {
  try {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 4500);

    const response = await fetch('https://ipapi.co/json/', {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });

    window.clearTimeout(timeout);
    if (!response.ok) return null;

    const data = (await response.json()) as {
      ip?: string;
      country_name?: string;
      country_code?: string;
      city?: string;
      region?: string;
      postal?: string;
      latitude?: number;
      longitude?: number;
      timezone?: string;
      org?: string;
      asn?: string;
    };

    return {
      ip: data.ip,
      country: data.country_name,
      countryCode: data.country_code,
      city: data.city,
      region: data.region,
      postal: data.postal,
      latitude: data.latitude,
      longitude: data.longitude,
      timezone: data.timezone,
      org: data.org,
      asn: data.asn,
    };
  } catch {
    return null;
  }
}

function buildClientMetadata(options: {
  termsAccepted: boolean;
  interestLabel: string | null;
  location: WaitlistLocationMetadata | null;
}): WaitlistMetadata {
  const ua = navigator.userAgent;
  const { browser, browserVersion } = parseBrowser(ua);
  const { os, osVersion } = parseOs(ua);
  const connection = readConnection();

  return {
    browser,
    browserVersion,
    device: detectDevice(ua),
    os,
    osVersion,
    userAgent: ua.substring(0, 1000),
    screenWidth: screen.width,
    screenHeight: screen.height,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio || 1,
    colorDepth: screen.colorDepth,
    language: navigator.language || 'en',
    languages: [...(navigator.languages ?? [navigator.language || 'en'])],
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffsetMinutes: new Date().getTimezoneOffset(),
    referrer: document.referrer || 'direct',
    landingPage: readLandingPage(),
    submissionPage: window.location.href,
    pageTitle: document.title,
    utm: readUtmParams(),
    cookieEnabled: navigator.cookieEnabled,
    online: navigator.onLine,
    hardwareConcurrency:
      typeof navigator.hardwareConcurrency === 'number' ? navigator.hardwareConcurrency : null,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    ...connection,
    prefersReducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    prefersColorScheme: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
    termsAccepted: options.termsAccepted,
    interestLabel: options.interestLabel,
    collectedAt: new Date().toISOString(),
    location: options.location,
  };
}

function rememberLandingPage() {
  if (typeof window === 'undefined') return;
  try {
    if (!sessionStorage.getItem('hg_landing_page')) {
      sessionStorage.setItem('hg_landing_page', window.location.href);
    }
  } catch {
    // sessionStorage may be unavailable in strict privacy modes
  }
}

function readLandingPage() {
  try {
    return sessionStorage.getItem('hg_landing_page') || window.location.href;
  } catch {
    return window.location.href;
  }
}

rememberLandingPage();

export async function collectWaitlistMetadata(options: {
  termsAccepted: boolean;
  interestLabel: string | null;
}): Promise<WaitlistMetadata> {
  const location = await getIpLocation();
  return buildClientMetadata({ ...options, location });
}

export async function submitWaitlist(data: {
  name: string;
  email: string;
  interest: WaitlistInterest;
  termsAccepted: boolean;
  interestLabel?: string | null;
}): Promise<{ ok: boolean; message: string }> {
  const payload: WaitlistPayload = {
    name: data.name,
    email: data.email,
    interest: data.interest,
    source: 'website',
    timestamp: new Date().toISOString(),
    metadata: await collectWaitlistMetadata({
      termsAccepted: data.termsAccepted,
      interestLabel: data.interestLabel ?? null,
    }),
  };

  const response = await fetch(SITE.waitlistApi, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  const body = (await response.json().catch(() => ({}))) as { message?: string };

  if (response.ok) {
    return { ok: true, message: "Thanks for joining! We'll be in touch soon." };
  }

  return {
    ok: false,
    message: body.message || 'Something went wrong. Please try again.',
  };
}
